import React, { useMemo } from 'react';
import { EFFECT_TYPE_LABELS_FI, effectColor, normalizeEffectType } from '../../config/effectTypes';
import { nodeCardHeight, NODE_CARD_WIDTH } from './nodeCardMetrics';
import { isStraightPair } from './straightPair';
import { orthPath, crossingsOnVertical } from './orthogonalRoute';

/**
 * CrossLinkLayer — links between branches, routed rather than drawn.
 *
 * These are real edges: a consequence with more than one cause. The tree can
 * only put a node under one parent, so the second cause has always been an
 * overlay — and as a curve laid over the map it was unreadable, which is why it
 * read as "you cannot draw those at all".
 *
 * Routed instead, and routed like the connectors it belongs among: out of the
 * source's right edge and into the target's left edge, exactly where the spine
 * arrives and leaves. In between it climbs to a lane in the gap above its row,
 * runs along to a lane in the gap left of the target, and comes down that. The
 * last stretch shares the target's own incoming corridor, so the link reads as
 * joining the line rather than landing on the box.
 *
 * Lanes are reserved by the layout, so no leg crosses a card. Where the
 * vertical leg crosses a spine connector it hops over it — a crossing and a
 * junction look identical otherwise.
 *
 * Dashed throughout: the spine is what the group built, and these are the
 * relations that did not fit in it.
 */

const CROSS_COLOR = 'rgba(150, 190, 245, 0.9)';

/**
 * Where the link leaves the source's connector: past the (+), which sits 36
 * from the card edge with a radius of 22. Branching at the edge itself put the
 * turn behind the button; branching after it makes the link visibly a second
 * road off the same connector.
 */
const BRANCH_AFTER_PLUS = NODE_CARD_WIDTH / 2 + 62;

/**
 * How far the link runs along the connector before it turns up. Without this
 * the route began with the turn itself, and a corner needs a leg on both sides
 * to be rounded — so the branch left the main line at a hard right angle while
 * every other corner on the route was soft.
 *
 * Kept short on purpose: the window between the (+) and the type pill is about
 * fifty units wide, and a longer lead turned up through the pill.
 */
const BRANCH_LEAD = 26;

export default function CrossLinkLayer({
  links = [],
  // Blocker → node, already resolved to boxes: a blocker card is not a node, so
  // it has no entry in `positions`. Always 'estää', and always left to right,
  // because the layout pushes what a blocker blocks past the blocker's column.
  blockLinks = [],
  nodes = [],
  positions = {},
  // Per link key: { laneA, laneB, laneX } in SVG coordinates.
  lanes = {},
  // The spine, for working out what the vertical leg has to hop over.
  spineArrows = [],
  colorEdgesByType = true,
  onTypeClick,
  selection = null,
  onSelect = null,
  // Route layout: links between lanes are freeform curves drawn under everything,
  // with no lanes and no hops — the chain's right-angled lines read in front of them.
  freeform = false,
}) {
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const routed = useMemo(() => links.map((link) => {
    const from = positions[link.from];
    const to = positions[link.to];
    const lane = lanes[`${link.from}->${link.to}`];
    if (!from || !to) return null;

    const type = normalizeEffectType(link.effectType);
    // "Sama asia" has no direction: it is one thing written down twice. So no
    // arrowhead either way, and between two cards in one column it is simply
    // the straight line from the bottom of the upper to the top of the lower.
    const symmetric = type === 'is_same';
    if (symmetric && isStraightPair(link.from, link.to, positions, nodes)) {
      const [upper, lower] = from.y <= to.y ? [link.from, link.to] : [link.to, link.from];
      const y0 = positions[upper].y + nodeCardHeight(nodeById.get(upper)) / 2;
      const y1 = positions[lower].y - nodeCardHeight(nodeById.get(lower)) / 2;
      return {
        key: `${link.from}->${link.to}`,
        link,
        d: `M ${from.x} ${y0} L ${from.x} ${y1}`,
        color: (colorEdgesByType && effectColor(type)) || CROSS_COLOR,
        label: EFFECT_TYPE_LABELS_FI[type],
        midX: from.x,
        midY: (y0 + y1) / 2,
        hopCount: 0,
        symmetric,
        selectKey: `${link.from}->${link.to}`,
        ids: [link.from, link.to],
        pair: { from: link.from, to: link.to },
      };
    }
    if (freeform) {
      const x0 = from.x + BRANCH_AFTER_PLUS;
      const x1 = to.x - NODE_CARD_WIDTH / 2;
      const pull = Math.max(80, Math.abs(x1 - x0) / 2);
      return {
        key: `${link.from}->${link.to}`,
        link,
        d: `M ${x0} ${from.y} C ${x0 + pull} ${from.y} ${x1 - pull} ${to.y} ${x1} ${to.y}`,
        color: (colorEdgesByType && type && effectColor(type)) || CROSS_COLOR,
        symmetric,
        label: (type && EFFECT_TYPE_LABELS_FI[type]) || 'liittyy',
        midX: (x0 + x1) / 2,
        midY: (from.y + to.y) / 2,
        hopCount: 0,
        selectKey: `${link.from}->${link.to}`,
        ids: [link.from, link.to],
        pair: { from: link.from, to: link.to },
      };
    }
    if (!lane) return null;

    // Off the source's own connector just past its (+), in at the target's left
    // edge — the same corridor every spine connector uses at both ends.
    const exitX = from.x + BRANCH_AFTER_PLUS;
    const entryX = to.x - NODE_CARD_WIDTH / 2;

    const points = [
      { x: exitX, y: from.y },
      { x: exitX + BRANCH_LEAD, y: from.y },
      { x: exitX + BRANCH_LEAD, y: lane.laneA },
      { x: lane.laneX, y: lane.laneA },
      { x: lane.laneX, y: to.y },
      { x: entryX, y: to.y },
    ];

    // Leg 4 is the long descent in the column gap; it is the only one that
    // crosses lines it does not join.
    const hops = {};
    const found = [];
    spineArrows.forEach((a) => {
      found.push(...crossingsOnVertical(a.path, lane.laneX, lane.laneA, to.y));
    });
    if (found.length) hops[4] = found;

    const color = (colorEdgesByType && type && effectColor(type)) || CROSS_COLOR;

    return {
      key: `${link.from}->${link.to}`,
      link,
      d: orthPath(points, { hops }),
      color,
      symmetric,
      label: (type && EFFECT_TYPE_LABELS_FI[type]) || 'liittyy',
      // On the horizontal lane out of the source, where there is always room.
      midX: (exitX + BRANCH_LEAD + lane.laneX) / 2,
      midY: lane.laneA,
      hopCount: (hops[4] || []).length,
      selectKey: `${link.from}->${link.to}`,
      ids: [link.from, link.to],
      pair: { from: link.from, to: link.to },
    };
  }).filter(Boolean), [links, positions, lanes, spineArrows, nodes, nodeById, colorEdgesByType, freeform]);

  // Out of the blocker's right edge, along the gap left of the target, into the
  // target's left edge. No row lane: the target is downstream by construction,
  // so the route never has to travel back over the map.
  const blocked = useMemo(() => blockLinks.map((link) => {
    // Always arrive at the target's left edge, the way every connector on the
    // map does. Going forward that means leaving the source's right edge;
    // going backward — a blocker reaching something upstream, which the layout
    // cannot push — it means leaving the LEFT edge and running down the outside.
    // Routing backward links as though they went forward sent the line back
    // through both cards.
    const forward = link.to.x > link.from.x + 1;
    const fromLeft = link.from.x - link.from.width / 2;
    const fromRight = link.from.x + link.from.width / 2;
    const toLeft = link.to.x - link.to.width / 2;

    const exitX = forward ? fromRight : fromLeft;
    const laneX = forward
      ? Math.max(toLeft - 85, fromRight + 40)
      : Math.min(toLeft - 85, fromLeft - 40);

    const points = [
      { x: exitX, y: link.from.y },
      { x: laneX, y: link.from.y },
      { x: laneX, y: link.to.y },
      { x: toLeft, y: link.to.y },
    ];

    const hops = {};
    const found = [];
    spineArrows.forEach((a) => {
      found.push(...crossingsOnVertical(a.path, laneX, link.from.y, link.to.y));
    });
    if (found.length) hops[2] = found;

    const type = normalizeEffectType(link.effectType);
    const color = link.blocks ? '#EF5350' : ((type && effectColor(type)) || CROSS_COLOR);
    const label = link.blocks
      ? 'estää'
      : ((type && EFFECT_TYPE_LABELS_FI[type]) || EFFECT_TYPE_LABELS_FI.leads_to);

    return {
      key: link.key,
      d: orthPath(points, { hops }),
      midX: (exitX + laneX) / 2,
      midY: link.from.y,
      color,
      label,
      selectKey: link.key,
      ids: [link.from.id, link.to.id],
    };
  }), [blockLinks, spineArrows]);

  const dimOf = (key) => (selection && selection.key !== key ? 0.09 : 1);
  const pick = (item) => (e) => {
    if (!onSelect) return;
    e.stopPropagation();
    onSelect({
      key: item.selectKey,
      ids: new Set(item.ids),
      ...(item.pair || {}),
      label: item.label,
      color: item.color,
    });
  };

  if (!routed.length && !blocked.length) return null;

  return (
    <g className="cross-link-layer">
      <defs>
        {blocked.map((b) => (
          <marker
            key={`bm-${b.key}`}
            id={`block-arrow-${b.key}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={b.color} />
          </marker>
        ))}
        {routed.map((r) => (
          <marker
            key={`m-${r.key}`}
            id={`cross-arrow-${r.key}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={r.color} />
          </marker>
        ))}
      </defs>

      {blocked.map((b) => {
        const pillWidth = b.label.length * 8.2 + 26;
        return (
          <g
            key={b.key}
            className="block-link"
            opacity={dimOf(b.selectKey)}
            style={{ transition: 'opacity 0.18s ease' }}
          >
            {onSelect && (
              <path d={b.d} stroke="transparent" strokeWidth={18} fill="none" style={{ cursor: 'pointer' }} onClick={pick(b)} />
            )}
            <path
              d={b.d}
              fill="none"
              stroke={b.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              markerEnd={`url(#block-arrow-${b.key})`}
              style={{ pointerEvents: 'none' }}
            />
            <g transform={`translate(${b.midX}, ${b.midY})`} style={{ pointerEvents: 'none' }}>
              <rect
                x={-pillWidth / 2}
                y={-12}
                width={pillWidth}
                height={24}
                rx={12}
                fill="#12121c"
                stroke={b.color}
                strokeWidth={1.5}
              />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12.5}
                fontWeight={700}
                fill={b.color}
                style={{ userSelect: 'none' }}
              >
                {b.label}
              </text>
            </g>
          </g>
        );
      })}

      {routed.map((r) => {
        const pillWidth = r.label.length * 8.2 + 26;
        return (
          <g key={r.key} className="cross-link" opacity={dimOf(r.selectKey)} style={{ transition: 'opacity 0.18s ease' }}>
            {/* A wide invisible stroke under the dashed one: a 2px line with
                gaps in it is not a target. */}
            {onSelect && (
              <path d={r.d} stroke="transparent" strokeWidth={18} fill="none" style={{ cursor: 'pointer' }} onClick={pick(r)} />
            )}
            <path
              d={r.d}
              fill="none"
              stroke={r.color}
              strokeOpacity={freeform ? 0.5 : 1}
              strokeWidth={2}
              strokeDasharray="7 5"
              strokeLinecap="round"
              markerEnd={r.symmetric ? undefined : `url(#cross-arrow-${r.key})`}
              style={{ pointerEvents: 'none' }}
            />
            <g
              className="cross-link-pill"
              transform={`translate(${r.midX}, ${r.midY})`}
              style={{ cursor: onTypeClick ? 'pointer' : 'default' }}
              onClick={(e) => {
                if (!onTypeClick) return;
                e.stopPropagation();
                const box = e.currentTarget.getBoundingClientRect();
                onTypeClick(r.link, { x: box.left + box.width / 2, y: box.top + box.height / 2 });
              }}
            >
              <rect
                x={-pillWidth / 2}
                y={-12}
                width={pillWidth}
                height={24}
                rx={12}
                fill="#12121c"
                stroke={r.color}
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12.5}
                fontWeight={600}
                fill={r.color}
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                {r.label}
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
}
